
import React from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { InstagramSettingsFormProps } from "./types";

const InstagramSettingsForm: React.FC<InstagramSettingsFormProps> = ({
  username,
  isContentSchedulingEnabled,
  onContentSchedulingChange,
  isAutoResponsesEnabled,
  onAutoResponsesChange,
  onDisconnect,
  aiStaffName = "Your AI Assistant"
}) => {
  return (
    <div className="space-y-4">
      <div className="bg-muted p-4 rounded-md">
        <div className="flex items-center space-x-2 mb-2">
          <span className="font-medium">Connected Account:</span>
          <span>@{username}</span>
        </div>
        <p className="text-sm text-muted-foreground">
          Your Instagram account is now connected to {aiStaffName}
        </p>
      </div>
      
      <Separator />
      
      <div className="space-y-4">
        <h4 className="text-sm font-medium">Integration Settings</h4>
        
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <label htmlFor="content-scheduling" className="text-sm">Content Scheduling</label>
            <p className="text-xs text-muted-foreground">
              Allow {aiStaffName} to schedule posts to Instagram
            </p>
          </div>
          <Switch 
            id="content-scheduling" 
            checked={isContentSchedulingEnabled} 
            onCheckedChange={onContentSchedulingChange}
          />
        </div>
        
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <label htmlFor="auto-responses" className="text-sm">Auto Responses</label>
            <p className="text-xs text-muted-foreground">
              Allow {aiStaffName} to respond to Instagram comments
            </p>
          </div>
          <Switch 
            id="auto-responses" 
            checked={isAutoResponsesEnabled} 
            onCheckedChange={onAutoResponsesChange}
          />
        </div>
        
        <Alert className="bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-900">
          <AlertCircle className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          <AlertDescription className="text-blue-700 dark:text-blue-400">
            For full automation capabilities, we recommend setting up the Instagram Creator Studio
          </AlertDescription>
        </Alert>
      </div>
      
      <Separator />
      
      <div className="flex justify-between items-center">
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => onDisconnect()}
          className="text-red-500 border-red-200 hover:bg-red-50 hover:text-red-600"
        >
          Disconnect
        </Button>
        
        <div className="flex items-center text-xs text-muted-foreground gap-1">
          <AlertCircle className="h-3 w-3" />
          <span>Changes are saved automatically</span>
        </div>
      </div>
    </div>
  );
};

export default InstagramSettingsForm;
